import { type Statement } from 'launchdarkly-api-typescript';

import { type CustomRole, type Member } from '../../../model';

export const deepClone = <T>(obj: T): T => JSON.parse(JSON.stringify(obj));

export const hasRoleAttributePlaceholders = (role: CustomRole): boolean => {
  const roleString = JSON.stringify(role);
  return roleString.includes('${roleAttribute/');
};

export const findPlaceholdersInResource = (resource: string): string[] => {
  const regex = /\$\{roleAttribute\/([^}]+)\}/g;
  return Array.from(resource.matchAll(regex), (match) => match[1]);
};

export const generateAllResourceCombinations = (attributeValues: string[][]): string[][] =>
  attributeValues.reduce<string[][]>(
    (previousCombinations, nextValues) =>
      previousCombinations.flatMap((combination) => nextValues.map((value) => combination.concat(value))),
    [[]],
  );

export const resolveResourcePlaceholders = (resource: string, roleAttributes: Record<string, string[]>): string[] => {
  const placeholders = findPlaceholdersInResource(resource);

  if (placeholders.length === 0) {
    return [resource];
  }

  const attributeValues = placeholders.map((placeholder) => roleAttributes[placeholder] ?? []);
  const valueCombinations = generateAllResourceCombinations(attributeValues);

  return valueCombinations.map((combination) => {
    let resolvedResource = resource;
    placeholders.forEach((placeholder, index) => {
      const placeholderPattern = `\${roleAttribute/${placeholder}}`;
      resolvedResource = resolvedResource.replace(placeholderPattern, combination[index]);
    });
    return resolvedResource;
  });
};

export const resolvePolicyItemResources = (
  policyItem: Statement,
  memberRoleAttributes: Record<string, string[]>,
): Statement => {
  if (!policyItem.resources || policyItem.resources.length === 0) {
    return policyItem;
  }

  const allResolvedResources = policyItem.resources.flatMap((resource: string) =>
    resolveResourcePlaceholders(resource, memberRoleAttributes),
  );

  return {
    ...policyItem,
    resources: allResolvedResources,
  };
};

export const createResolvedRole = (
  role: CustomRole,
  member: Member,
  teamRoleAttributes?: { [key: string]: string[] },
): CustomRole => {
  const roleAttributes = teamRoleAttributes || member?.roleAttributes || {};

  try {
    type DeepWriteable<T> = { -readonly [P in keyof T]: DeepWriteable<T[P]> };
    const resolvedRole: DeepWriteable<CustomRole> = deepClone(role);

    if (!resolvedRole.policy || !Array.isArray(resolvedRole.policy)) {
      return resolvedRole;
    }

    resolvedRole.policy = resolvedRole.policy.map((policyItem) =>
      resolvePolicyItemResources(policyItem, roleAttributes),
    );

    if (resolvedRole._presetStatements && Array.isArray(resolvedRole._presetStatements)) {
      resolvedRole._presetStatements = resolvedRole._presetStatements.map((policyItem) =>
        resolvePolicyItemResources(policyItem, roleAttributes),
      );
    }

    return resolvedRole as CustomRole;
  } catch (_error) {
    return role;
  }
};
